/**
 * Webhook Reducer
 * State management for webhooks
 */
import { createReducer, on } from '@ngrx/store';
import { Webhook, WebhookDelivery, TestWebhookResponse } from '../models/webhook.models';
import * as WebhookActions from './webhook.actions';

/**
 * Webhook State Interface
 */
export interface WebhookState {
  webhooks: Webhook[];
  selectedWebhook: Webhook | null;
  deliveries: WebhookDelivery[];
  createdWebhookSecret: string | null; // Shown only once after creation
  testResult: TestWebhookResponse | null;
  
  // Pagination
  totalWebhooks: number;
  currentPage: number;
  pageSize: number;
  totalDeliveries: number;
  deliveriesPage: number;
  deliveriesPageSize: number;
  
  // Loading states
  loading: boolean;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
  testing: boolean;
  loadingDeliveries: boolean;
  
  // Error state
  error: string | null;
}

/**
 * Initial State
 */
export const initialState: WebhookState = {
  webhooks: [],
  selectedWebhook: null,
  deliveries: [],
  createdWebhookSecret: null,
  testResult: null,
  
  totalWebhooks: 0,
  currentPage: 1,
  pageSize: 20,
  totalDeliveries: 0,
  deliveriesPage: 1,
  deliveriesPageSize: 20,
  
  loading: false,
  creating: false,
  updating: false,
  deleting: false,
  testing: false,
  loadingDeliveries: false,
  
  error: null
};

/**
 * Webhook Reducer
 */
export const webhookReducer = createReducer(
  initialState,

  // Load Webhooks
  on(WebhookActions.loadWebhooks, (state) => ({
    ...state,
    loading: true,
    error: null
  })),
  on(WebhookActions.loadWebhooksSuccess, (state, { webhooks, total, page, pageSize }) => ({
    ...state,
    webhooks,
    totalWebhooks: total,
    currentPage: page,
    pageSize,
    loading: false,
    error: null
  })),
  on(WebhookActions.loadWebhooksFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),

  // Load Single Webhook
  on(WebhookActions.loadWebhook, (state) => ({
    ...state,
    loading: true,
    error: null
  })),
  on(WebhookActions.loadWebhookSuccess, (state, { webhook }) => ({
    ...state,
    selectedWebhook: webhook,
    loading: false,
    error: null
  })),
  on(WebhookActions.loadWebhookFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),

  // Create Webhook
  on(WebhookActions.createWebhook, (state) => ({
    ...state,
    creating: true,
    error: null,
    createdWebhookSecret: null
  })),
  on(WebhookActions.createWebhookSuccess, (state, { response }) => ({
    ...state,
    webhooks: [response.webhook, ...state.webhooks],
    createdWebhookSecret: response.secret,
    totalWebhooks: state.totalWebhooks + 1,
    creating: false,
    error: null
  })),
  on(WebhookActions.createWebhookFailure, (state, { error }) => ({
    ...state,
    creating: false,
    error
  })),

  // Update Webhook
  on(WebhookActions.updateWebhook, (state) => ({
    ...state,
    updating: true,
    error: null
  })),
  on(WebhookActions.updateWebhookSuccess, (state, { webhook }) => ({
    ...state,
    webhooks: state.webhooks.map(w => w._id === webhook._id ? webhook : w),
    selectedWebhook: state.selectedWebhook?._id === webhook._id ? webhook : state.selectedWebhook,
    updating: false,
    error: null
  })),
  on(WebhookActions.updateWebhookFailure, (state, { error }) => ({
    ...state,
    updating: false,
    error
  })),

  // Delete Webhook
  on(WebhookActions.deleteWebhook, (state) => ({
    ...state,
    deleting: true,
    error: null
  })),
  on(WebhookActions.deleteWebhookSuccess, (state, { webhookId }) => ({
    ...state,
    webhooks: state.webhooks.filter(w => w._id !== webhookId),
    selectedWebhook: state.selectedWebhook?._id === webhookId ? null : state.selectedWebhook,
    totalWebhooks: state.totalWebhooks - 1,
    deleting: false,
    error: null
  })),
  on(WebhookActions.deleteWebhookFailure, (state, { error }) => ({
    ...state,
    deleting: false,
    error
  })),

  // Test Webhook
  on(WebhookActions.testWebhook, (state) => ({
    ...state,
    testing: true,
    error: null,
    testResult: null
  })),
  on(WebhookActions.testWebhookSuccess, (state, { response }) => ({
    ...state,
    testResult: response,
    testing: false,
    error: null
  })),
  on(WebhookActions.testWebhookFailure, (state, { error }) => ({
    ...state,
    testing: false,
    error
  })),

  // Load Webhook Deliveries
  on(WebhookActions.loadWebhookDeliveries, (state) => ({
    ...state,
    loadingDeliveries: true,
    error: null
  })),
  on(WebhookActions.loadWebhookDeliveriesSuccess, (state, { deliveries, total, page, pageSize }) => ({
    ...state,
    deliveries,
    totalDeliveries: total,
    deliveriesPage: page,
    deliveriesPageSize: pageSize,
    loadingDeliveries: false,
    error: null
  })),
  on(WebhookActions.loadWebhookDeliveriesFailure, (state, { error }) => ({
    ...state,
    loadingDeliveries: false,
    error
  })),

  // Clear Webhook Secret
  on(WebhookActions.clearWebhookSecret, (state) => ({
    ...state,
    createdWebhookSecret: null
  })),

  // Clear Test Result
  on(WebhookActions.clearTestResult, (state) => ({
    ...state,
    testResult: null
  }))
);

